/** The four stores of the one canonical rolling save. */
export const STORES = ['meta', 'checkpoints', 'journal', 'owner'] as const;
export type StoreName = (typeof STORES)[number];

export interface StorageSnapshot {
  get(store: StoreName, key: string): unknown;
  all(store: StoreName): Array<{ key: string; value: unknown }>;
}

/**
 * A write body runs inside one atomic transaction over every store: if it throws, nothing is
 * kept. Bodies may await transaction requests only — never a timer or another transaction.
 */
export interface StorageTransaction {
  get(store: StoreName, key: string): Promise<unknown>;
  all(store: StoreName): Promise<Array<{ key: string; value: unknown }>>;
  put(store: StoreName, key: string, value: unknown): Promise<void>;
  remove(store: StoreName, key: string): Promise<void>;
}

export interface StorageUpgrade {
  createStore(store: StoreName): void;
}

export interface StorageDriver {
  readonly name: string;
  open(upgrade: (schema: StorageUpgrade) => void): Promise<void>;
  close(): void;
  read<T>(body: (snapshot: StorageSnapshot) => T): Promise<T>;
  write(body: (tx: StorageTransaction) => Promise<void>): Promise<void>;
}

/** Recognize a browser storage-quota refusal from either engine's spelling. */
export function isQuotaError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED';
}
